import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Message,
  ConversationSummary,
  ConversationTurn,
  FeedbackCheckResponse,
  UserFeedbackSubmission,
  Site,
} from '../types';
import { fetchRecentConversations, fetchConversationTurns } from '../services/api';
import { getErrorMessage } from '../utils/errorUtils';
import { CHAT_CONVERSATION_PARAM, CHAT_HISTORY_PARAM, CHAT_HISTORY_PARAM_VALUE } from '../constants';
import { useSiteContext } from './SiteContext';
import i18n from '../i18n';

/**
 * Rebuild the flat `Message[]` transcript from persisted conversation turns.
 * Pure (no React state) so both the click-driven resume path and the
 * URL-driven sync path produce identical transcripts.
 */
function buildResumedMessages(
  turns: ConversationTurn[],
  conversationId: string,
  site: Site,
): Message[] {
  const isOrchestrator = (site.domain_type ?? 'standard') === 'orchestrator';
  const messages: Message[] = [];
  for (const turn of turns) {
    messages.push({
      id: `msg_${turn.turn_number}_q`,
      role: 'user',
      content: turn.question,
      timestamp: new Date(turn.created_at),
    });

    // Rebuild FeedbackCheckResponse from persisted LLM-judge scores
    // (hidden for orchestrator domains — see AnalyticsPanel.isOrchestrator)
    let qualityScore: FeedbackCheckResponse | undefined;
    if (!isOrchestrator && turn.quality_score != null && turn.verdict) {
      qualityScore = {
        success: true,
        quality_score: turn.quality_score,
        verdict: turn.verdict as 'good' | 'needs_improvement' | 'poor',
        relevance: turn.relevance_score != null
          ? { score: turn.relevance_score, reasoning: turn.relevance_reasoning || '' } : null,
        completeness: turn.completeness_score != null
          ? { score: turn.completeness_score, reasoning: turn.completeness_reasoning || '' } : null,
        faithfulness: turn.faithfulness_score != null
          ? { score: turn.faithfulness_score, reasoning: turn.faithfulness_reasoning || '' } : null,
        coherence: turn.coherence_score != null
          ? { score: turn.coherence_score, reasoning: turn.coherence_reasoning || '' } : null,
        overall_reasoning: turn.overall_reasoning || '',
        llm_duration_ms: 0,
        site_id: site.site_id,
        org_id: site.org_id,
      };
    }

    // Rebuild UserFeedbackSubmission from persisted user feedback
    // (hidden for orchestrator domains — see AnalyticsPanel.isOrchestrator)
    let userFeedback: UserFeedbackSubmission | undefined;
    if (!isOrchestrator && turn.user_relevance_score != null) {
      userFeedback = {
        relevance: {
          score: turn.user_relevance_score,
          reasoning: turn.user_relevance_reasoning || '',
        },
        completeness: {
          score: turn.user_completeness_score || 0,
          reasoning: turn.user_completeness_reasoning || '',
        },
        faithfulness: {
          score: turn.user_faithfulness_score || 0,
          reasoning: turn.user_faithfulness_reasoning || '',
        },
        coherence: {
          score: turn.user_coherence_score || 0,
          reasoning: turn.user_coherence_reasoning || '',
        },
      };
    }

    messages.push({
      id: `msg_${turn.turn_number}_a`,
      role: 'assistant',
      content: turn.answer,
      timestamp: new Date(turn.created_at),
      qualityScore,
      conversationId,
      turnNumber: turn.turn_number,
      finalDocumentList: turn.final_document_list,
      answered: turn.answered ?? true,
      clarification: turn.clarification === true,
      clarificationOptions: turn.clarification_options?.length
        ? turn.clarification_options
        : undefined,
      suggestedAlternativeQuestions: turn.suggested_alternative_questions?.length
        ? turn.suggested_alternative_questions
        : undefined,
      userFeedback,
      answerReasoning: turn.answer_reasoning || undefined,
    });
  }
  return messages;
}

interface ChatContextValue {
  recentChats: ConversationSummary[];
  activeConversationId: string | null;
  resumedMessages: Message[] | undefined;
  chatKey: number;

  refreshRecentChats: () => Promise<void>;
  handleNewChat: () => void;
  handleStartChat: () => void;
  handleResumeChat: (conversationId: string) => Promise<void>;
  /** Hydrate state to match the URL's `conversation` param without navigating.
   * Pass null to reset to the blank new-chat state. */
  syncActiveConversation: (conversationId: string | null) => Promise<void>;
  /** Record an id the backend just assigned to a brand-new chat, without
   * re-fetching turns (the transcript already lives in ChatBox state). */
  markConversationAssigned: (conversationId: string) => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function useChatContext(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used within a ChatProvider');
  return ctx;
}

interface ChatProviderProps {
  children: React.ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { selectedSite, setSitesError } = useSiteContext();
  const navigate = useNavigate();

  const [recentChats, setRecentChats] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [resumedMessages, setResumedMessages] = useState<Message[] | undefined>(undefined);
  const [chatKey, setChatKey] = useState(0);
  // Most-recently-requested conversation id, shared by the click-driven resume
  // and URL-driven sync paths. Guards against a stale fetch (rapid
  // back/forward) overwriting a newer request's transcript.
  const pendingConversationRef = useRef<string | null>(null);
  // Id the backend just assigned to a brand-new chat's first reply, whose
  // transcript is already live in ChatBox. Marks that id as "adopt from the URL
  // without re-fetching" so the URL-sync effect neither refetches nor remounts
  // when the replace-nav lands. Best-effort, cleared once adopted or reset.
  const assignedConversationRef = useRef<string | null>(null);

  // Reset chat state when site changes
  useEffect(() => {
    setActiveConversationId(null);
    setResumedMessages(undefined);
  }, [selectedSite?.site_id]);

  // Load recent conversations when site is selected
  const refreshRecentChats = useCallback(async () => {
    if (!selectedSite) {
      setRecentChats([]);
      return;
    }
    try {
      const conversations = await fetchRecentConversations(
        selectedSite.site_id,
        selectedSite.org_id,
        10,
      );
      setRecentChats(conversations);
    } catch {
      setRecentChats([]);
    }
  }, [selectedSite]);

  useEffect(() => {
    refreshRecentChats();
  }, [refreshRecentChats]);

  // Clear to the blank new-chat state (no navigation). Invalidates any
  // in-flight conversation fetch so its late result is discarded.
  const resetToNewChat = useCallback(() => {
    pendingConversationRef.current = null;
    assignedConversationRef.current = null;
    setActiveConversationId(null);
    setResumedMessages(undefined);
    setChatKey(prev => prev + 1);
  }, []);

  // Fetch a conversation's turns and set it active (no navigation). Returns
  // true if this request's result was applied, false if it was superseded by
  // a newer request. Throws if the fetch fails (bad/deleted/foreign id).
  const hydrateConversation = useCallback(async (conversationId: string): Promise<boolean> => {
    if (!selectedSite) return false;
    pendingConversationRef.current = conversationId;
    const turns = await fetchConversationTurns(
      selectedSite.site_id,
      conversationId,
      selectedSite.org_id,
    );
    if (pendingConversationRef.current !== conversationId) return false;
    setActiveConversationId(conversationId);
    setResumedMessages(buildResumedMessages(turns, conversationId, selectedSite));
    setChatKey(prev => prev + 1);
    return true;
  }, [selectedSite]);

  const handleNewChat = useCallback(() => {
    if (!selectedSite) return;
    resetToNewChat();
    navigate(`/domains/${selectedSite.site_id}/chat`);
  }, [selectedSite, navigate, resetToNewChat]);

  const handleStartChat = useCallback(() => {
    if (!selectedSite) return;
    resetToNewChat();
    navigate(`/domains/${selectedSite.site_id}/chat`);
  }, [selectedSite, navigate, resetToNewChat]);

  const handleResumeChat = useCallback(async (conversationId: string) => {
    if (!selectedSite) return;
    try {
      const applied = await hydrateConversation(conversationId);
      if (!applied) return;
      navigate(
        `/domains/${selectedSite.site_id}/chat`
        + `?${CHAT_HISTORY_PARAM}=${CHAT_HISTORY_PARAM_VALUE}`
        + `&${CHAT_CONVERSATION_PARAM}=${conversationId}`,
      );
    } catch (error) {
      setSitesError(getErrorMessage(error, i18n.t('errors.chat.resumeFailed')));
    }
  }, [selectedSite, navigate, setSitesError, hydrateConversation]);

  const syncActiveConversation = useCallback(async (conversationId: string | null) => {
    if (!selectedSite) return;
    if (conversationId === null) {
      resetToNewChat();
      return;
    }
    // Brand-new chat's first reply: the URL just gained this id via replace-nav
    // and the transcript is already live in ChatBox. Adopt it (so the effect's
    // guard settles) WITHOUT bumping chatKey — bumping would remount ChatBox and
    // wipe the just-sent exchange (the flicker we are fixing).
    if (conversationId === assignedConversationRef.current) {
      assignedConversationRef.current = null;
      pendingConversationRef.current = conversationId;
      setActiveConversationId(conversationId);
      // Backfill resumedMessages in the background (still NO chatKey bump -> the
      // live ChatBox is untouched). Without this, activeConversationId is set but
      // resumedMessages stays undefined; if ChatBox later remounts (navigate away
      // and back to /chat?conversation=<id>) it would show the empty new-chat
      // state, and the URL-sync guard (url === active) would block re-hydration.
      try {
        const turns = await fetchConversationTurns(
          selectedSite.site_id, conversationId, selectedSite.org_id,
        );
        if (pendingConversationRef.current === conversationId) {
          setResumedMessages(buildResumedMessages(turns, conversationId, selectedSite));
        }
      } catch {
        // Best-effort: the live transcript still shows now; a failed backfill
        // only affects a later remount, and a genuinely broken id would fail the
        // normal hydrate path too.
      }
      return;
    }
    try {
      await hydrateConversation(conversationId);
    } catch {
      // Bad/deleted/foreign id in the URL: fall back to a fresh chat and
      // replace-navigate to strip the id, so we neither loop nor show a
      // broken transcript.
      resetToNewChat();
      navigate(`/domains/${selectedSite.site_id}/chat`, { replace: true });
    }
  }, [selectedSite, hydrateConversation, resetToNewChat, navigate]);

  const markConversationAssigned = useCallback((conversationId: string) => {
    // Record the assignment only; activeConversationId is set later, by
    // syncActiveConversation, once the replace-nav lands the id in the URL.
    // Setting it here (before the URL updates) makes the URL-sync effect
    // momentarily see active != url and reset/remount the live chat mid-turn.
    assignedConversationRef.current = conversationId;
    pendingConversationRef.current = conversationId;
  }, []);

  const value = useMemo<ChatContextValue>(() => ({
    recentChats,
    activeConversationId,
    resumedMessages,
    chatKey,
    refreshRecentChats,
    handleNewChat,
    handleStartChat,
    handleResumeChat,
    syncActiveConversation,
    markConversationAssigned,
  }), [
    recentChats, activeConversationId, resumedMessages, chatKey,
    refreshRecentChats, handleNewChat, handleStartChat, handleResumeChat,
    syncActiveConversation, markConversationAssigned,
  ]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
